import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Complaint, ComplaintPosition, DocumentAttachment, State } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { SyncService, getSelectedComplaint, getDocumentPositions } from '@icc/b2b/sales-document';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { core, logger } from '@icc/shared';
import { ComplaintService } from '../complaint.service';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
    selector: 'icc-complaint-position-dialog',
    templateUrl: './complaint-position-dialog.component.html',
    styleUrls: ['./complaint-position-dialog.component.scss'],
})
export class ComplaintPositionDialogComponent implements OnInit, OnDestroy {

    /**
     * When true, the dialog is blocked because external changes were detected.
     */
    isBlocked = false;

    /**
     * Flag indicating that a permission-affecting change was detected externally.
     * When true, the next store update will trigger a blocking check.
     */
    private pendingBlockCheck = false;

    /**
     * Timestamp when the dialog was opened.
     */
    private dialogOpenedAt = 0;

    /**
     * Initial position values when dialog was opened.
     * Used to detect which fields user has modified.
     */
    private initialPosition!: ComplaintPosition;

    /**
     * Cached value of whether user has manufacturer permission.
     * Loaded once on init since permissions don't change during dialog lifetime.
     */
    private hasManufacturerPermission = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            position: ComplaintPosition;
            complaint: Complaint;
            attachments: DocumentAttachment[];
        },
        private store: Store<State>,
        private complaintService: ComplaintService,
        private syncService: SyncService,
        private permissionsService: NgxPermissionsService,
        private cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit(): Promise<void> {
        this.dialogOpenedAt = Date.now();

        // Cache manufacturer permission - doesn't change during dialog lifetime
        this.hasManufacturerPermission = await this.permissionsService.hasPermission('complaints-manage-manufacturer');

        // Store initial position values to detect user changes
        this.initialPosition = core.copy(this.data.position);

        this.setupExternalChangesDetection();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    /**
     * Sets up detection of external changes.
     * Blocks the dialog only if an external change causes loss of edit permission.
     */
    private setupExternalChangesDetection(): void {
        // Fields that affect edit permission
        const permissionFields = [
            'assignedTo',
            'distributorStatusId',
            'manufacturerStatusId',
            'authorizedRetailerStatusId',
        ];

        this.subscriptions.push(
            // Subscribe to external changes - set flag when permission-affecting changes detected
            this.syncService.externalChanges$.subscribe(({
                documentIds,
                documentFields,
                timestamp
            }) => {
                // Ignore changes that happened before dialog was opened
                if (timestamp <= this.dialogOpenedAt) {
                    return;
                }

                // Check if this complaint was modified
                if (!documentIds.has(this.data.complaint.uuid)) {
                    return;
                }

                // If permission-affecting field changed, check immediately
                if (permissionFields.some(field => documentFields.has(field))) {
                    logger.log('[ComplaintPositionDialog] Permission field changed externally');
                    this.pendingBlockCheck = true;
                    // Check immediately with current data
                    if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                        logger.log('[ComplaintPositionDialog] BLOCKING - external change removed permission');
                        this.isBlocked = true;
                        this.cdr.detectChanges();
                    }
                }
            }),

            // Subscribe to complaint document changes - update data and check blocking if needed
            this.store.select(getSelectedComplaint).subscribe((complaint) => {
                if (complaint && complaint.uuid === this.data.complaint.uuid) {
                    Object.assign(this.data.complaint, complaint);

                    // If we have a pending block check, perform it now with updated data
                    if (this.pendingBlockCheck && !this.isBlocked) {
                        if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                            logger.log('[ComplaintPositionDialog] BLOCKING - external change removed permission');
                            this.isBlocked = true;
                            this.cdr.detectChanges();
                        }
                    }
                }
            }),

            // Subscribe to position changes from store - update only fields not modified by user
            this.store.select(getDocumentPositions(this.data.complaint.uuid)).subscribe((positions) => {
                if (!this.isBlocked) {
                    const updatedPosition = positions.find(
                        (pos) => pos.uuid === this.data.position.uuid
                    ) as ComplaintPosition | undefined;
                    if (updatedPosition) {
                        // Update only fields that haven't been changed by the user
                        this.updateUnchangedFields(updatedPosition);
                    }
                }
            }),
        );
    }

    /**
     * Checks if the current status allows editing.
     */
    private currentStatusAllowEdit(): boolean {
        const userType = this.complaintService.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.data.complaint, userType);

        if (statusId === 0 || isNaN(statusId)) {
            return true;
        }

        const status = this.complaintService.statuses.find((s) => s.id === statusId);
        return status?.allowEdit ?? false;
    }

    /**
     * Checks if the current user is the assigned party or has manufacturer permission.
     * Users with 'complaints-manage-manufacturer' permission bypass the assignment check.
     */
    private isUserAssigned(): boolean {
        if (this.hasManufacturerPermission) {
            return true;
        }
        const userType = this.complaintService.getUserType();
        return this.data.complaint.assignedTo?.startsWith(userType) ?? false;
    }

    /**
     * Updates position fields that haven't been modified by the user.
     * Compares current value with initial value to detect user changes.
     */
    private updateUnchangedFields(updatedPosition: ComplaintPosition): void {
        const fieldsToCheck: (keyof ComplaintPosition)[] = [
            'complaintProductTypeId',
            'complaintProductType',
            'complaintDocumentType',
            'serialNumber',
            'invoiceNumber',
            'orderNumber',
            'orderPositionNumber',
            'distributorOrderNumber',
            'distributorOrderPositionNumber',
            'defectDate',
            'configuration',
            'description',
        ];

        for (const field of fieldsToCheck) {
            // Check if current value equals initial value (user hasn't changed it)
            const currentValue = this.data.position[field];
            const initialValue = this.initialPosition[field];

            if (JSON.stringify(currentValue) === JSON.stringify(initialValue)) {
                // Field not modified by user, safe to update from sync
                // For objects like 'configuration', create a deep copy to trigger Angular change detection
                if (field === 'configuration' && updatedPosition[field]) {
                    (this.data.position as any)[field] = core.copy(updatedPosition[field]);
                } else {
                    (this.data.position as any)[field] = updatedPosition[field];
                }
            }
        }
    }

    async save() {
        this.dialogRef.close({
            position: this.data.position,
            attachments: this.data.attachments,
        });
    }

    close() {
        this.dialogRef.close();
    }
}
